import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  Button,
  FormField,
  Header,
  Select,
  SelectProps,
  SpaceBetween,
} from "@amzn/awsui-components-console";

import { TACT_COUNTRIES } from "src/forms/IPL/options";

import { resolveIplTpa } from "./iplCountryRouting";
import type { IplPreScreenData } from "./types";

interface IplPreScreenProps {
  /**
   * Invoked when the user submits the IPL pre-screen with a country
   * selected. Receives the captured `{ country, resolvedTpa }` payload
   * that the orchestrator persists to `state.jsonForms.preScreen.ipl`
   * and uses to build the IPL schema name (`IPL-<resolvedTpa>-v1_0_0`).
   */
  onPreScreenSubmit: (data: IplPreScreenData) => void;
}

/**
 * IPL pre-screen rendered by `JsonFormsClaimIntakeApp` for the `ipl` LOB.
 *
 * Renders a single Cloudscape `Select` populated from `TACT_COUNTRIES`.
 * Submit is disabled until the user selects a country; on submit, the
 * component routes the country through `resolveIplTpa` and invokes
 * `onPreScreenSubmit` with `{ country, resolvedTpa }`.
 */
function IplPreScreen({ onPreScreenSubmit }: IplPreScreenProps): JSX.Element {
  const [selectedOption, setSelectedOption] =
    useState<SelectProps.Option | null>(null);

  const options = useMemo<SelectProps.Option[]>(
    () =>
      TACT_COUNTRIES.map((country) => ({
        label: country.label,
        value: country.value,
      })),
    []
  );

  const handleChange = useCallback(
    ({ detail }: { detail: SelectProps.ChangeDetail }) => {
      setSelectedOption(detail.selectedOption);
    },
    []
  );

  const handleSubmit = useCallback(() => {
    const country = selectedOption?.value;
    if (!country) return;
    onPreScreenSubmit({
      country,
      resolvedTpa: resolveIplTpa(country),
    });
  }, [onPreScreenSubmit, selectedOption]);

  const isSubmitDisabled = !selectedOption?.value;

  return (
    <Box padding="l" data-testid="ipl-pre-screen">
      <SpaceBetween size="m" direction="vertical">
        <Header variant="h2">IPL claim intake</Header>
        <FormField
          label="Country"
          description="Select the country where the incident occurred."
          data-testid="ipl-pre-screen-country-form-field"
        >
          <Select
            data-testid="ipl-pre-screen-country-select"
            options={options}
            selectedOption={selectedOption}
            onChange={handleChange}
            placeholder="Select a country"
          />
        </FormField>
        <Button
          variant="primary"
          disabled={isSubmitDisabled}
          onClick={handleSubmit}
          data-testid="ipl-pre-screen-submit-button"
        >
          Continue
        </Button>
      </SpaceBetween>
    </Box>
  );
}

export default IplPreScreen;
